import { CreditCardIcon, ReceiptIcon, RepeatIcon } from '@phosphor-icons/react';
import { useMemo, type ComponentType } from 'react';
import type { RecurringGroupKey } from '@/lib/api';
import { useChartSeries } from '@/lib/theme/useThemeColors';

export interface TaxonomyConfig {
  key: RecurringGroupKey;
  label: string;
  icon: ComponentType<{ size?: number; weight?: 'bold' | 'regular' | 'duotone' | 'fill'; className?: string }>;
}

// Kind identity (icon + label) only — color is resolved separately via
// useRecurringTaxonomyColors() so it stays reactive to the live theme and the
// icon chip / legend dot / chart bar for a given kind always match (see that
// hook for the palette rationale).
export const RECURRING_TAXONOMY: Record<RecurringGroupKey, TaxonomyConfig> = {
  parcelamentos: { key: 'parcelamentos', label: 'Parcelamentos', icon: CreditCardIcon },
  assinaturas: { key: 'assinaturas', label: 'Assinaturas', icon: RepeatIcon },
  fixas: { key: 'fixas', label: 'Contas fixas', icon: ReceiptIcon },
};

/** Group render order on the page. */
export const RECURRING_GROUP_ORDER: RecurringGroupKey[] = ['parcelamentos', 'assinaturas', 'fixas'];

// Indices into the app's --chart-1..10 categorical series (0-based), picked to
// land on clay · plum · slate-blue — a coordinated 3-kind identity distinct
// from green/teal/gray (reserved: income/success · cool-income · uncategorized).
const CHART_INDEX: Record<RecurringGroupKey, number> = {
  parcelamentos: 1, // --chart-2
  assinaturas: 8, // --chart-9
  fixas: 6, // --chart-7
};

/**
 * Reactive per-kind color for the ECharts bars, legend dots, and icon chips —
 * resolved from the theme's chart series (not a frozen hex) so it follows a
 * live `[data-theme]` flip, and every consumer stays visually consistent for
 * the same kind since they all read from here. Memoized on `series` (itself
 * stable unless the theme changes) so consumers can safely list the returned
 * object in their own `useMemo`/`useEffect` deps without invalidating on every render.
 */
export function useRecurringTaxonomyColors(): Record<RecurringGroupKey, string> {
  const series = useChartSeries();
  return useMemo(
    () => ({
      parcelamentos: series[CHART_INDEX.parcelamentos],
      assinaturas: series[CHART_INDEX.assinaturas],
      fixas: series[CHART_INDEX.fixas],
    }),
    [series],
  );
}
